import { Link, useNavigate } from "react-router-dom";
import { Mail, MapPin, ArrowUpRight } from "lucide-react";
import { BRAND, NAV_LINKS } from "@/data/content";

const Footer = ({ onContact }) => {
  const year = new Date().getFullYear();
  const navigate = useNavigate();
  const go = (href) => {
    if (href.startsWith("/")) {
      navigate(href);
      return;
    }
    const el = document.querySelector(href);
    if (!el) return;
    if (window.lenis) window.lenis.scrollTo(el, { offset: -80 });
    else el.scrollIntoView({ behavior: "smooth" });
    window.history.replaceState(null, "", `/${href}`);
  };

  return (
    <footer data-testid="site-footer" className="relative bg-[#050A14] border-t border-white/10 overflow-hidden">
      {/* CTA band */}
      <div className="max-w-7xl mx-auto px-5 md:px-8 pt-24 pb-16">
        <div className="grid md:grid-cols-12 gap-10 items-center border-b border-white/10 pb-16">
          <div className="md:col-span-8">
            <h2 className="font-display font-bold tracking-tight text-white text-3xl md:text-5xl leading-[1.05]">
              Ready to turn your website into your best salesperson?
            </h2>
          </div>
          <div className="md:col-span-4 md:text-right">
            <button
              onClick={onContact}
              data-testid="footer-plan-button"
              className="group inline-flex items-center gap-2 rounded-full bg-[#1E6BFF] hover:bg-[#3B82F6] text-white font-semibold px-8 py-4 transition-colors duration-200 glow-blue"
            >
              Get a Free Website Plan
              <ArrowUpRight size={18} className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </button>
          </div>
        </div>

        {/* Links */}
        <div className="grid md:grid-cols-12 gap-10 py-16">
          <div className="md:col-span-5">
            <div className="flex items-center gap-3 mb-5">
              <img src={BRAND.logo} alt={BRAND.name} className="h-12 w-12 rounded-lg object-contain" />
              <span className="font-display font-bold text-xl text-white">
                TITAN<span className="text-[#1E6BFF]">.</span>
              </span>
            </div>
            <p className="text-gray-400 max-w-xs leading-relaxed">{BRAND.tagline} Websites that make small businesses look — and sell — big.</p>
          </div>

          <div className="md:col-span-3">
            <div className="text-xs uppercase tracking-[0.2em] text-gray-500 mb-5 font-semibold">Explore</div>
            <ul className="space-y-3">
              {NAV_LINKS.map((l) => (
                <li key={l.href}>
                  <button
                    onClick={() => go(l.href)}
                    data-testid={`footer-link-${l.label.toLowerCase()}`}
                    className="text-gray-300 hover:text-[#1E6BFF] transition-colors duration-200"
                  >
                    {l.label}
                  </button>
                </li>
              ))}
            </ul>
          </div>

          <div className="md:col-span-4">
            <div className="text-xs uppercase tracking-[0.2em] text-gray-500 mb-5 font-semibold">Get in touch</div>
            <ul className="space-y-3 text-gray-300">
              <li className="flex items-center gap-3">
                <Mail size={16} className="text-[#1E6BFF]" />
                <a href={`mailto:${BRAND.email}`} className="hover:text-white transition-colors">{BRAND.email}</a>
              </li>
              <li className="flex items-center gap-3">
                <MapPin size={16} className="text-[#1E6BFF]" />
                {BRAND.location}
              </li>
            </ul>
          </div>
        </div>
      </div>

      {/* Massive brand word */}
      <div className="relative select-none pointer-events-none">
        <div className="font-display font-black text-outline text-center leading-none tracking-tighter text-[24vw] md:text-[20vw]">
          TITAN
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-5 md:px-8 py-8 flex flex-col sm:flex-row items-center justify-between gap-3 border-t border-white/10">
        <p className="text-sm text-gray-500">© {year} {BRAND.name}. All rights reserved.</p>
        <p className="text-sm text-gray-500">Designed &amp; built in-house.</p>
      </div>
    </footer>
  );
};

export default Footer;
